import { ErrorCode } from '@approvalmax/types';
import { PageWrapper, toast } from '@approvalmax/ui/src/components';
import { useAtom } from 'jotai';
import { memo, useEffect } from 'react';
import { usePrevious } from 'react-use';
import { AccountingDomainEntitiesSynchronizationStatus, useGetQBooksBudgets } from 'shared/data/webApp/v2';
import { useCompanyBasedOnUrl } from 'shared/hooks/useCompanyBasedOnUrl';

import Loading from '../../../loading';
import { syncQBooksBudgetsPageState } from '../../states';
import { BudgetListWrapper } from '../BudgetListWrapper/BudgetListWrapper';
import { Toolbar } from '../Toolbar/Toolbar';
import { BudgetList } from './components/BudgetList/BudgetList';
import { messages } from './QBooksBudgets.messages';

export const QBooksBudgets = memo(() => {
    const company = useCompanyBasedOnUrl();

    const { data, isInitialLoading, isError, error, isRefetching } = useGetQBooksBudgets(
        {
            path: {
                companyId: company.id,
            },
        },
        {
            enabled: company.flags.hasActiveIntegration,
            refetchInterval: (data) => {
                const hasSyncInProgressBudget = data?.items.some(
                    (item) => item.synchronizationStatus === AccountingDomainEntitiesSynchronizationStatus.in_progress
                );

                const noBudgets = data && data.items.length === 0;

                if (data?.isSyncEnabled && (hasSyncInProgressBudget || noBudgets)) {
                    return 10000;
                }

                return false;
            },
        }
    );

    const isDisconnected = !company.flags.hasActiveIntegration || error?.code === ErrorCode.IntegrationMustBeActive;

    const hasBudgets = Boolean(data?.items?.length);

    const hasSyncInProgressBudget = data?.items.some(
        (item) => item.synchronizationStatus === AccountingDomainEntitiesSynchronizationStatus.in_progress
    );

    const prevIsRefetching = usePrevious(isRefetching);

    const [syncQBooksBudgetsState, setSyncQBooksBudgetsState] = useAtom(syncQBooksBudgetsPageState);

    useEffect(() => {
        if (syncQBooksBudgetsState && prevIsRefetching && !isRefetching && !hasSyncInProgressBudget) {
            toast.success(messages.syncSuccessful);

            setSyncQBooksBudgetsState(false);
        }
    }, [prevIsRefetching, isRefetching, hasSyncInProgressBudget, syncQBooksBudgetsState, setSyncQBooksBudgetsState]);

    if (isInitialLoading) {
        return <Loading />;
    }

    return (
        <>
            <Toolbar
                canUploadBudget={false}
                showSettings
                isBudgetSyncEnabled={data?.isSyncEnabled}
                isDisabled={isError}
                isSyncInProgress={hasSyncInProgressBudget}
            />

            <PageWrapper.Content>
                <BudgetListWrapper isDisconnected={isDisconnected} isError={isError} hasBudgets={hasBudgets}>
                    <BudgetList budgets={data?.items ?? []} isBudgetSyncEnabled={data?.isSyncEnabled} />
                </BudgetListWrapper>
            </PageWrapper.Content>
        </>
    );
});

QBooksBudgets.displayName = 'QBooksBudgets';
